import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Download } from 'lucide-react';
import { personalInfo, skills, projects } from '../mockData';
import SkillCard from '../components/SkillCard';
import ProjectCard from '../components/ProjectCard';
import { Button } from '../components/ui/button';

const Home = () => {
  const featuredProjects = projects.filter(p => p.featured);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="pt-40 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-neutral-900 border border-neutral-800 rounded-full text-sm text-neutral-400 mb-6">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              Available for opportunities
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-tight">
              {personalInfo.name}
            </h1>
            
            <div className="flex flex-wrap gap-3 text-lg md:text-xl text-neutral-400 mb-6">
              <span>{personalInfo.title}</span>
              <span className="text-neutral-700">·</span>
              <span>{personalInfo.subtitle}</span>
            </div>

            <p className="text-xl md:text-2xl text-neutral-300 mb-8 leading-relaxed">
              {personalInfo.tagline}
            </p>

            <p className="text-neutral-400 mb-10 leading-relaxed max-w-2xl">
              {personalInfo.description}
            </p>

            <div className="flex flex-wrap gap-4">
              <Link to="/projects">
                <Button className="bg-cyan-500 hover:bg-cyan-600 text-black font-medium px-6 py-2 rounded-lg transition-colors">
                  View Projects
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-neutral-700 text-white hover:bg-neutral-900 px-6 py-2 rounded-lg transition-colors">
                  Get in Touch
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 px-6 bg-neutral-950/50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Technical Expertise</h2>
            <p className="text-neutral-400 max-w-2xl">
              Building secure, scalable systems with a focus on backend architecture, security, and performance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {skills.map((skill) => (
              <SkillCard key={skill.id} skill={skill} />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Featured Projects</h2>
              <p className="text-neutral-400 max-w-2xl">
                Selected work showcasing security-focused systems, full-stack applications, and backend architecture.
              </p>
            </div>
            <Link to="/projects" className="hidden md:block text-cyan-400 hover:text-cyan-300 transition-colors font-medium">
              View All Projects →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <Link to="/projects" className="text-cyan-400 hover:text-cyan-300 transition-colors font-medium">
              View All Projects →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-neutral-950/50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Let's Build Something</h2>
          <p className="text-neutral-400 mb-8 max-w-2xl mx-auto">
            Looking for a developer who understands systems, security, and scalability? Let's talk.
          </p>
          <Link to="/contact">
            <Button className="bg-cyan-500 hover:bg-cyan-600 text-black font-medium px-8 py-3 rounded-lg transition-colors">
              Get in Touch
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
